import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
  FieldErrors,
} from 'react-hook-form';
import { createSpecialLeaveFormSchema, type CreateSpecialLeaveFormValues } from '@/schemas/absence';
import { absenceService } from '@/api/domains/absence/absence.service';
import { ABSENCE_SCOPE_LIST_REFRESH_EVENT } from '@/api/domains/absence/absence.types';
import type {
  LC_SpecialLeaveTypeItemOut,
  LC_AbsenceWorkdayTypeItemOut,
  EmployeeForAbsence,
} from '@/api/domains/absence/absence.types';
import { useUI } from '@/hooks/ui';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { useDebounce } from '@/hooks/ui/useDebounce';
import type { SelectorOption } from '@/components/ui/inputs/selector';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';
import { injectValidationErrors } from '@/utils';


const PAGE_SIZE = 20;
const CALCULATED_DATES_DEBOUNCE_MS = 400;
const CONFLICT_DATE_OVERLAP_CODE = 'CONFLICT_DATE_OVERLAP';
const CONFLICT_EMPLOYEE_BLOCKED_CODE = 'CONFLICT_EMPLOYEE_BLOCKED';

export type PermitType = '' | 'special' | 'administrative' | 'fraction';

export interface UseCreateSpecialLeaveResult {
  // Form
  form: {
    register: UseFormRegister<CreateSpecialLeaveFormValues>;
    setValue: UseFormSetValue<CreateSpecialLeaveFormValues>;
    watch: UseFormWatch<CreateSpecialLeaveFormValues>;
    errors: FieldErrors<CreateSpecialLeaveFormValues>;
    trigger: UseFormTrigger<CreateSpecialLeaveFormValues>;
  };
  isSubmitted: boolean;
  isSubmitting: boolean;
  handleSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;

  // Employee selector
  employeeOptions: SelectorOption[];
  isSelectedEmployeeSynced: boolean;
  isLoadingEmployees: boolean;
  isLoadingMoreEmployees: boolean;
  employeeHasMore: boolean;
  handleEmployeeSearch: (search: string) => void;
  handleEmployeeChange: (value: string | string[]) => void;
  loadMoreEmployees: () => Promise<void>;

  // Permit subtype selector (Permiso especial / administrativo / fracción)
  permitType: PermitType;
  permitTypeOptions: SelectorOption[];
  handlePermitTypeChange: (value: string | string[]) => void;

  // Special leave types
  specialLeaveTypeOptions: SelectorOption[];
  isLoadingSpecialLeaveTypes: boolean;

  // Workday types
  workdayTypeOptions: SelectorOption[];
  isLoadingWorkdayTypes: boolean;
}

export function useCreateSpecialLeave(): UseCreateSpecialLeaveResult {
  const { t: translate } = useTranslation('absence');
  const { showApiError, showSuccess, showNotification, closeSidePanel } = useUI();
  const { setLoading } = useSidePanelLoading();

  // ── Permit subtype selector ─────────────────────────────────────────────────
  // Las tres opciones están habilitadas por defecto. Cuando el colaborador
  // seleccionado tiene un contrato activo sincronizado con Rex+, solo se lista
  // "Permiso fracción" (ver permitTypeOptions más abajo, tras el selector de colaborador).
  const [permitType, setPermitType] = useState<PermitType>('');

  const handlePermitTypeChange = useCallback((value: string | string[]) => {
    const stringValue = Array.isArray(value) ? (value[0] ?? '') : value;
    setPermitType(stringValue as PermitType);
  }, []);

  // ── Special leave types ─────────────────────────────────────────────────────
  // Nota: no se usa AbortController aquí — es un catálogo pequeño de una sola pasada;
  // abortarlo en el cleanup generaría TaskCanceledException en el backend durante los
  // doble-montajes de React.StrictMode en desarrollo (ver useCatalogLoader.ts).
  const [specialLeaveTypes, setSpecialLeaveTypes] = useState<LC_SpecialLeaveTypeItemOut[]>([]);
  const [isLoadingSpecialLeaveTypes, setIsLoadingSpecialLeaveTypes] = useState(false);

  const loadSpecialLeaveTypes = useCallback(async () => {
    setIsLoadingSpecialLeaveTypes(true);
    try {
      const response = await absenceService.getSpecialLeaveTypes();
      if (response.success && response.data) {
        setSpecialLeaveTypes(response.data ?? []);
      } else {
        showApiError(response, translate('specialLeave.errors.loadSpecialLeaveTypesFailed'));
      }
    } finally {
      setIsLoadingSpecialLeaveTypes(false);
    }
  }, [showApiError, translate]);

  useEffect(() => {
    void loadSpecialLeaveTypes();
  }, [loadSpecialLeaveTypes]);

  const specialLeaveTypeOptions = useMemo<SelectorOption[]>(
    () => specialLeaveTypes.map((type) => ({ value: String(type.id), label: type.name })),
    [specialLeaveTypes],
  );

  // ── Workday types ──────────────────────────────────────────────────────────
  // Mismo motivo que loadSpecialLeaveTypes: catálogo estático, sin AbortController.
  const [workdayTypes, setWorkdayTypes] = useState<LC_AbsenceWorkdayTypeItemOut[]>([]);
  const [isLoadingWorkdayTypes, setIsLoadingWorkdayTypes] = useState(false);

  const loadWorkdayTypes = useCallback(async () => {
    setIsLoadingWorkdayTypes(true);
    try {
      const response = await absenceService.getAbsenceWorkdayTypes();
      if (response.success && response.data) {
        setWorkdayTypes(response.data ?? []);
      } else {
        showApiError(response, translate('specialLeave.errors.loadWorkdayTypesFailed'));
      }
    } finally {
      setIsLoadingWorkdayTypes(false);
    }
  }, [showApiError, translate]);

  useEffect(() => {
    void loadWorkdayTypes();
  }, [loadWorkdayTypes]);

  const workdayTypeOptions = useMemo<SelectorOption[]>(
    () => workdayTypes.map((type) => ({ value: type.code, label: type.label })),
    [workdayTypes],
  );

  // ── Employee selector ──────────────────────────────────────────────────────
  const [employees, setEmployees] = useState<EmployeeForAbsence[]>([]);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeeHasMore, setEmployeeHasMore] = useState(false);
  const [employeeSearchText, setEmployeeSearchText] = useState('');
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isLoadingMoreEmployees, setIsLoadingMoreEmployees] = useState(false);
  const selectedEmployeeRef = useRef<EmployeeForAbsence | null>(null);

  const loadEmployees = useCallback(
    async (page: number, search: string, append: boolean = false) => {
      if (append) {
        setIsLoadingMoreEmployees(true);
      } else {
        setIsLoadingEmployees(true);
      }
      try {
        const response = await absenceService.getEmployeesForAbsence({
          page,
          pageSize: PAGE_SIZE,
          searchText: search || undefined,
        });

        if (response.success && response.data) {
          const newItems = response.data.data ?? [];
          setEmployees((previous) => {
            let result = append ? [...previous, ...newItems] : newItems;
            const selected = selectedEmployeeRef.current;
            if (selected && !result.some((employee) => employee.id === selected.id)) {
              result = [selected, ...result];
            }
            return result;
          });
          setEmployeeHasMore(response.data.hasNextPage);
          setEmployeePage(page);
        } else {
          showApiError(response, translate('specialLeave.errors.loadEmployeesFailed'));
        }
      } finally {
        if (append) {
          setIsLoadingMoreEmployees(false);
        } else {
          setIsLoadingEmployees(false);
        }
      }
    },
    [showApiError, translate],
  );

  useEffect(() => {
    void loadEmployees(1, '');
  }, [loadEmployees]);

  const loadMoreEmployees = useCallback(async () => {
    if (!isLoadingMoreEmployees && employeeHasMore) {
      await loadEmployees(employeePage + 1, employeeSearchText, true);
    }
  }, [isLoadingMoreEmployees, employeeHasMore, employeePage, employeeSearchText, loadEmployees]);

  const handleEmployeeSearch = useCallback(
    (search: string) => {
      setEmployeeSearchText(search);
      void loadEmployees(1, search);
    },
    [loadEmployees],
  );

  const employeeOptions = useMemo<SelectorOption[]>(
    () =>
      employees.map((employee) => ({
        value: String(employee.id),
        label: employee.fullName
          ? `${employee.fullName}${employee.employeeTaxIdentificationNumber ? ` (${employee.employeeTaxIdentificationNumber})` : ''}`
          : String(employee.id),
        supportingText: employee.companyName || undefined,
      })),
    [employees],
  );

  // ── Loading state ──────────────────────────────────────────────────────────
  const initialLoadDoneRef = useRef(false);
  const areCatalogsLoaded = useMemo(
    () => !isLoadingEmployees && !isLoadingSpecialLeaveTypes && !isLoadingWorkdayTypes,
    [isLoadingEmployees, isLoadingSpecialLeaveTypes, isLoadingWorkdayTypes],
  );

  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      setLoading(!areCatalogsLoaded);
      if (areCatalogsLoaded) {
        initialLoadDoneRef.current = true;
      }
    }
  }, [areCatalogsLoaded, setLoading]);

  // ── Form ───────────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const overlapErrorBaselineRef = useRef<{ startDate: string; endDate: string } | null>(null);

  const {
    register,
    setValue,
    watch,
    trigger,
    setError,
    clearErrors,
    handleSubmit: rhfHandleSubmit,
    formState,
  } = useForm<CreateSpecialLeaveFormValues>({
    resolver: zodResolver(createSpecialLeaveFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      employeeTargetId: null,
      specialLeaveTypeId: null,
      startDate: '',
      endDate: '',
      workdayType: undefined as unknown as string,
      dayCount: null,
      returnDate: '',
      detail: '',
    },
  });

  // ── Permit subtype options — derivadas del colaborador seleccionado ─────────
  // Si el colaborador posee un contrato activo sincronizado con Rex+ (isBlocked),
  // solo se permite ingresar "Permiso fracción"; las demás opciones ni siquiera se listan.
  const selectedEmployeeTargetId = watch('employeeTargetId');
  const isSelectedEmployeeSynced = useMemo(
    () => employees.some(
      (employee) => employee.id === selectedEmployeeTargetId && employee.isBlocked,
    ),
    [employees, selectedEmployeeTargetId],
  );

  const permitTypeOptions = useMemo<SelectorOption[]>(() => {
    if (isSelectedEmployeeSynced) {
      return [{ value: 'fraction', label: translate('specialLeave.modal.options.fraction.title') }];
    }
    return [
      { value: 'special', label: translate('specialLeave.modal.options.special.title') },
      { value: 'administrative', label: translate('specialLeave.modal.options.administrative.title') },
      { value: 'fraction', label: translate('specialLeave.modal.options.fraction.title') },
    ];
  }, [isSelectedEmployeeSynced, translate]);

  // Si el colaborador cambia a uno sincronizado mientras había un tipo de permiso
  // no permitido seleccionado, se limpia para evitar enviar un valor ya oculto.
  useEffect(() => {
    if (isSelectedEmployeeSynced && (permitType === 'special' || permitType === 'administrative')) {
      setPermitType('');
      setValue('specialLeaveTypeId', null, { shouldDirty: true });
    }
  }, [isSelectedEmployeeSynced, permitType, setValue]);

  // Fecha de término y fecha de retorno se calculan en el backend a partir de fecha de inicio +
  // cantidad de días (mismo patrón que useCreateVacation.calculateDates).
  const calculatedDatesRequestIdRef = useRef(0);

  const employeeTargetIdForDates = watch('employeeTargetId');
  const startDateForDates = watch('startDate');
  const dayCountForDates = watch('dayCount');
  const workdayTypeForDates = watch('workdayType');
  const endDateValue = watch('endDate');

  // Los cuatro insumos comparten el mismo retardo: así una edición que toca varios campos dispara
  // una sola petición, y ninguna sale con una combinación a medio actualizar.
  const debouncedEmployeeTargetIdForDates = useDebounce(
    employeeTargetIdForDates, CALCULATED_DATES_DEBOUNCE_MS,
  );
  const debouncedStartDateForDates = useDebounce(startDateForDates, CALCULATED_DATES_DEBOUNCE_MS);
  const debouncedDayCountForDates = useDebounce(dayCountForDates, CALCULATED_DATES_DEBOUNCE_MS);
  const debouncedWorkdayTypeForDates = useDebounce(
    workdayTypeForDates, CALCULATED_DATES_DEBOUNCE_MS,
  );

  const calculateDates = useCallback(
    async (params: {
      employeeTargetId: number;
      startDate: string;
      dayCount: number;
      workdayType: string;
    }) => {
      const requestId = ++calculatedDatesRequestIdRef.current;
      const response = await absenceService.calculateAbsenceDates(params);
      if (requestId !== calculatedDatesRequestIdRef.current) return; // respuesta obsoleta, ignorar
      if (response.success && response.data) {
        setValue('endDate', response.data.endDate, { shouldDirty: true, shouldValidate: true });
        setValue('returnDate', response.data.returnDate, { shouldDirty: true });
      } else {
        // Sin esto, las fechas del cálculo anterior sobreviven al error y, como el esquema
        // solo exige que no estén vacías, Guardar quedaría habilitado con un período que ya
        // no corresponde a los insumos actuales.
        setValue('endDate', '');
        setValue('returnDate', '');
        showApiError(response, translate('specialLeave.errors.calculateDatesFailed'));
      }
    },
    [setValue, showApiError, translate],
  );

  useEffect(() => {
    const numericDayCount = Number(debouncedDayCountForDates);
    if (
      !debouncedEmployeeTargetIdForDates ||
      !debouncedStartDateForDates ||
      !debouncedWorkdayTypeForDates ||
      !Number.isInteger(numericDayCount) ||
      numericDayCount <= 0
    ) {
      setValue('endDate', '');
      setValue('returnDate', '');
      return;
    }
    void calculateDates({
      employeeTargetId: debouncedEmployeeTargetIdForDates as number,
      startDate: debouncedStartDateForDates,
      dayCount: numericDayCount,
      workdayType: debouncedWorkdayTypeForDates,
    });
  }, [
    debouncedEmployeeTargetIdForDates,
    debouncedStartDateForDates,
    debouncedDayCountForDates,
    debouncedWorkdayTypeForDates,
    calculateDates,
    setValue,
  ]);

  // Si el usuario cambia las fechas tras un error de solapamiento, se limpia para evitar confusión.
  useEffect(() => {
    const baseline = overlapErrorBaselineRef.current;
    if (!baseline) return;
    if (baseline.startDate !== startDateForDates || baseline.endDate !== endDateValue) {
      overlapErrorBaselineRef.current = null;
      clearErrors(['startDate', 'endDate']);
    }
  }, [startDateForDates, endDateValue, clearErrors]);

  const onValid = useCallback(
    async (data: CreateSpecialLeaveFormValues) => {
      setIsSubmitting(true);
      try {
        const response = await absenceService.createSpecialLeave({
          employeeTargetId: data.employeeTargetId as number,
          specialLeaveTypeId: data.specialLeaveTypeId as number,
          startDate: data.startDate,
          workdayType: data.workdayType,
          dayCount: data.dayCount as number,
          detail: data.detail || undefined,
        });

        if (response.success) {
          showSuccess(translate('specialLeave.messages.createSuccess'));
          window.dispatchEvent(new CustomEvent(ABSENCE_SCOPE_LIST_REFRESH_EVENT));
          closeSidePanel?.();
        } else if (response.error?.code === CONFLICT_DATE_OVERLAP_CODE) {
          overlapErrorBaselineRef.current = { startDate: data.startDate, endDate: data.endDate };
          setError('startDate', { message: 'absence:specialLeave.errors.dateOverlap' });
          setError('endDate', { message: 'absence:specialLeave.errors.dateOverlap' });
          showNotification('error', translate('specialLeave.errors.dateOverlap'));
        } else if (response.error?.code === CONFLICT_EMPLOYEE_BLOCKED_CODE) {
          showNotification('error', translate('specialLeave.form.blockedEmployeeTooltip'));
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          injectValidationErrors(response.error.validationErrors, { setError });
        } else {
          showApiError(response, translate('specialLeave.errors.createFailed'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [closeSidePanel, setError, showApiError, showNotification, showSuccess, translate],
  );

  const handleEmployeeChange = useCallback(
    (value: string | string[]) => {
      const stringValue = Array.isArray(value) ? (value[0] ?? '') : value;
      const numericId = normalizeNumericSelectValue(stringValue);
      if (numericId !== undefined) {
        setValue('employeeTargetId', numericId as number, { shouldDirty: true });
        const found = employees.find((employee) => employee.id === numericId);
        if (found) {
          selectedEmployeeRef.current = found;
        }
      }
    },
    [employees, setValue],
  );

  const handleSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }
      const wrappedValid = (data: CreateSpecialLeaveFormValues) => { void onValid(data); };
      const wrappedInvalid = () => {};
      const runner = rhfHandleSubmit(wrappedValid, wrappedInvalid);
      void runner(event);
    },
    [rhfHandleSubmit, onValid],
  );

  return {
    form: {
      register,
      setValue,
      watch,
      errors: formState.errors,
      trigger,
    },
    isSubmitted: formState.isSubmitted,
    isSubmitting,
    handleSubmit,
    employeeOptions,
    isSelectedEmployeeSynced,
    isLoadingEmployees,
    isLoadingMoreEmployees,
    employeeHasMore,
    handleEmployeeSearch,
    handleEmployeeChange,
    loadMoreEmployees,
    permitType,
    permitTypeOptions,
    handlePermitTypeChange,
    specialLeaveTypeOptions,
    isLoadingSpecialLeaveTypes,
    workdayTypeOptions,
    isLoadingWorkdayTypes,
  };
}

export default useCreateSpecialLeave;
